import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import {
  UserPlus, ShieldCheck, Trash2, Eye, EyeOff, CheckSquare, Square,
  Users as UsersIcon, FileText, Wallet, Megaphone, Settings as CogIcon,
  Info, KeyRound, CreditCard, MoreVertical, Edit3, Plus, Minus, X, Loader2, Check,
  Ban, Play,
} from "lucide-react";
import { PERM_GROUPS, ALL_PERM_KEYS } from "../../lib/permissions";

// iter182 — new 25-key catalogue. Old 8-key rows remain accepted by
// the backend via LEGACY_EXPANSION so existing admins keep working.
const GROUP_ICON = {
  users:         UsersIcon,
  listings:      FileText,
  subscriptions: CreditCard,
  finance:       Wallet,
  marketing:     Megaphone,
  admin:         CogIcon,
  // iter225 — new "sections" group. Reuse CogIcon so no new icon
  // import is needed (the icon is decorative only).
  sections:      CogIcon,
};

// Flattened list for lookups.
const PERMS = PERM_GROUPS.flatMap((g) => g.perms);
const permMeta = (k) => PERMS.find((p) => p.key === k);

export default function AdminsTab() {
  const { lang } = useI18n();
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    permissions: [],
    must_change_password: true, // sent as extra field; backend ignores unknown keys safely
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const load = () => apiClient.get("/admin/admins")
    .then(({ data }) => setItems(data.items || []))
    .catch(e => setError(e.response?.data?.detail || ""));
  useEffect(() => { load(); }, []);

  const isSuper = form.role === "super_admin";

  // Effective permissions on the wire: super_admin always ships the full
  // set. Admin uses whatever the operator ticked.
  const effectivePermissions = useMemo(
    () => (isSuper ? ALL_PERM_KEYS : form.permissions),
    [isSuper, form.permissions],
  );

  const togglePerm = (p) => {
    if (isSuper) return;
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(p)
        ? f.permissions.filter(x => x !== p)
        : [...f.permissions, p],
    }));
  };

  const selectAll = () => {
    if (isSuper) return;
    setForm(f => ({ ...f, permissions: ALL_PERM_KEYS.slice() }));
  };
  const clearAll = () => {
    if (isSuper) return;
    setForm(f => ({ ...f, permissions: [] }));
  };

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await apiClient.post("/admin/admins", { ...form, permissions: effectivePermissions });
      setForm({ name: "", email: "", password: "", role: "admin", permissions: [], must_change_password: true });
      setShowPassword(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Error");
    }
  };

  const roleHint = isSuper
    ? (lang === "ar"
        ? "Super Admin: يمتلك جميع الصلاحيات ولا يمكن تقييدها."
        : "Super Admin: has every permission, cannot be restricted.")
    : (lang === "ar"
        ? "Admin: صلاحيات حسب الاختيارات أدناه."
        : "Admin: only the permissions you tick below.");

  const countLabel = (n) =>
    lang === "ar"
      ? `مُحدَّد ${n} من ${ALL_PERM_KEYS.length}`
      : `${n} of ${ALL_PERM_KEYS.length} selected`;

  return (
    <div data-testid="admin-admins" className="space-y-6">
      <h2 className="font-display text-2xl flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#8a6f2e]" />
        {lang === "ar" ? "المسؤولون والصلاحيات" : "Admins & permissions"} ({items.length})
      </h2>

      {error && (
        <div className="text-sm text-red-700 surface rounded-lg p-3" data-testid="new-admin-error">
          {typeof error === "string" ? error : (error.ar || error.en || "Error")}
        </div>
      )}

      {/* ── Create Admin form ─────────────────────────────────────── */}
      <form
        onSubmit={create}
        className="surface rounded-2xl p-5 sm:p-7 space-y-5 sm:space-y-6"
        data-testid="create-admin-form"
      >
        <div className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#8a6f2e]" />
          <h3 className="font-display text-lg sm:text-xl">
            {lang === "ar" ? "إنشاء حساب إداري" : "Create admin"}
          </h3>
        </div>

        {/* Identity */}
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          <label className="block">
            <span className="text-[12px] font-semibold text-[#404040] mb-1 block">
              {lang === "ar" ? "الاسم" : "Full name"}
            </span>
            <input
              className="input-dark w-full"
              placeholder={lang === "ar" ? "مثال: سالم القحطاني" : "e.g. Salem Alqahtani"}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="new-admin-name"
            />
          </label>

          <label className="block">
            <span className="text-[12px] font-semibold text-[#404040] mb-1 block">
              {lang === "ar" ? "البريد الإلكتروني" : "Email"}
            </span>
            <input
              className="input-dark w-full"
              type="email"
              placeholder="email@cartal.sa"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              data-testid="new-admin-email"
            />
          </label>

          {/* Password with show/hide */}
          <label className="block">
            <span className="text-[12px] font-semibold text-[#404040] mb-1 block">
              {lang === "ar" ? "كلمة المرور" : "Password"}
            </span>
            <div className="relative">
              <KeyRound className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
              <input
                className="input-dark w-full ps-9 pe-10"
                type={showPassword ? "text" : "password"}
                placeholder={lang === "ar" ? "8 أحرف على الأقل" : "At least 8 characters"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="new-admin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute end-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#0a0a0a]/5"
                aria-label={lang === "ar" ? "إظهار/إخفاء كلمة المرور" : "Show/Hide password"}
                data-testid="toggle-new-admin-password"
              >
                {showPassword
                  ? <EyeOff className="w-4 h-4 text-[#6b6b6b]" />
                  : <Eye className="w-4 h-4 text-[#6b6b6b]" />}
              </button>
            </div>
          </label>

          {/* Role */}
          <label className="block">
            <span className="text-[12px] font-semibold text-[#404040] mb-1 block">
              {lang === "ar" ? "النوع" : "Role"}
            </span>
            <select
              className="input-dark w-full"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="new-admin-role"
            >
              <option value="admin">admin</option>
              <option value="super_admin">super_admin</option>
            </select>
          </label>
        </div>

        {/* Role hint */}
        <div
          className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-[12px] leading-relaxed"
          style={{
            background: isSuper ? "rgba(201,165,92,0.10)" : "rgba(10,10,10,0.03)",
            border: `1px solid ${isSuper ? "rgba(201,165,92,0.35)" : "rgba(10,10,10,0.08)"}`,
            color: isSuper ? "#5a4615" : "#525252",
          }}
          data-testid="role-hint"
        >
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{roleHint}</span>
        </div>

        {/* Force change on first login */}
        <label
          className="flex items-start gap-2.5 cursor-pointer select-none text-[13px] leading-relaxed"
          data-testid="must-change-password-wrapper"
        >
          <input
            type="checkbox"
            className="accent-[#c9a55c] mt-0.5"
            checked={form.must_change_password}
            onChange={(e) => setForm({ ...form, must_change_password: e.target.checked })}
            data-testid="must-change-password"
          />
          <span className="text-[#3a3a3a]">
            {lang === "ar"
              ? "إجبار المسؤول على تغيير كلمة المرور عند أول تسجيل دخول."
              : "Force this admin to change their password on first sign-in."}
          </span>
        </label>

        {/* ── Permissions section ───────────────────────────────── */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#8a6f2e]" />
              <div className="font-semibold text-[13px] text-[#0a0a0a]">
                {lang === "ar" ? "الصلاحيات" : "Permissions"}
              </div>
              <span className="text-[11px] text-[#6b6b6b]" data-testid="perms-count">
                {countLabel(effectivePermissions.length)}
              </span>
            </div>

            {!isSuper && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-[12px] font-medium inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#0a0a0a]/[0.10] hover:border-[#c9a55c]/50 hover:bg-[#fbf7ec]"
                  data-testid="perms-select-all"
                >
                  <CheckSquare className="w-3.5 h-3.5 text-[#8a6f2e]" />
                  {lang === "ar" ? "تحديد الكل" : "Select all"}
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[12px] font-medium inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#0a0a0a]/[0.10] hover:border-red-300 hover:bg-red-50"
                  data-testid="perms-clear-all"
                >
                  <Square className="w-3.5 h-3.5 text-[#525252]" />
                  {lang === "ar" ? "إلغاء تحديد الكل" : "Deselect all"}
                </button>
              </div>
            )}
          </div>

          {isSuper ? (
            <div
              className="rounded-xl p-4 sm:p-5 flex items-start gap-3"
              style={{
                background: "linear-gradient(135deg, rgba(201,165,92,0.10), rgba(201,165,92,0.04))",
                border: "1px solid rgba(201,165,92,0.30)",
              }}
              data-testid="super-admin-badge"
            >
              <ShieldCheck className="w-5 h-5 text-[#8a6f2e] shrink-0 mt-0.5" />
              <div className="text-[13px] text-[#3a3a3a] leading-relaxed">
                {lang === "ar"
                  ? "بصلاحية Super Admin يحصل الحساب تلقائياً على جميع الصلاحيات ولا يمكن تقييدها من هنا."
                  : "As a Super Admin this account is automatically granted every permission and can't be restricted here."}
              </div>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {PERM_GROUPS.map((g) => {
                const Icon = GROUP_ICON[g.id] || UsersIcon;
                const groupPerms = g.perms;
                const selectedIn = groupPerms.filter((p) => form.permissions.includes(p.key)).length;
                return (
                  <div
                    key={g.id}
                    className="rounded-xl border border-[#0a0a0a]/[0.08] bg-white p-3.5 sm:p-4"
                    data-testid={`perm-group-${g.id}`}
                  >
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-[#fbf7ec] flex items-center justify-center">
                          <Icon className="w-3.5 h-3.5 text-[#8a6f2e]" />
                        </div>
                        <div className="font-semibold text-[13px] text-[#0a0a0a]">
                          {lang === "ar" ? g.ar : g.en}
                        </div>
                      </div>
                      <div className="text-[11px] text-[#8a8a8a]">
                        {selectedIn}/{groupPerms.length}
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      {groupPerms.map((p) => {
                        const checked = form.permissions.includes(p.key);
                        return (
                          <label
                            key={p.key}
                            className={`flex items-center gap-2 text-[13px] cursor-pointer px-2.5 py-2 rounded-lg border transition-colors ${
                              checked
                                ? "border-[#c9a55c]/50 bg-[#fbf7ec]"
                                : "border-[#0a0a0a]/[0.06] hover:border-[#c9a55c]/30 hover:bg-[#fafaf7]"
                            }`}
                            data-testid={`perm-item-${p.key}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePerm(p.key)}
                              className="accent-[#c9a55c]"
                            />
                            <span className="text-[#3a3a3a]">
                              {lang === "ar" ? p.ar : p.en}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="pt-1">
          <button
            type="submit"
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid="new-admin-create"
          >
            <UserPlus className="w-4 h-4" />
            {lang === "ar" ? "إنشاء" : "Create"}
          </button>
        </div>
      </form>

      {/* Admins list — desktop table + mobile cards, both driven by AdminRow */}
      <div className="hidden sm:block surface rounded-xl overflow-hidden" data-testid="admins-table-desktop">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a]/[0.04] text-[#3a3a3a] text-xs uppercase tracking-widest">
            <tr>
              <th className="p-3 text-start">{lang === "ar" ? "الاسم" : "Name"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "البريد" : "Email"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الدور" : "Role"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الصلاحيات" : "Permissions"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الحالة" : "Status"}</th>
              <th className="p-3 text-end">{lang === "ar" ? "إجراء" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(a => (
              <AdminRow
                key={a.id}
                a={a}
                lang={lang}
                currentUser={currentUser}
                allAdmins={items}
                onReload={load}
                mode="row"
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile — stacked cards */}
      <div className="sm:hidden space-y-3" data-testid="admins-cards-mobile">
        {items.map((a) => (
          <AdminRow
            key={a.id}
            a={a}
            lang={lang}
            currentUser={currentUser}
            allAdmins={items}
            onReload={load}
            mode="card"
          />
        ))}
      </div>
    </div>
  );
}

// ── AdminRow ────────────────────────────────────────────────────────
// Renders either a table row (desktop) or a card (mobile). Both share
// the same action menu (⋮) with Edit / Add-perm / Remove-perm / Delete.
// super_admin rows never expose add/remove-perm because they hold every
// permission implicitly.
function AdminRow({ a, lang, currentUser, allAdmins, onReload, mode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // "edit" | "add" | "remove" | "delete"
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const isSuper = a.role === "super_admin";
  const isSelf = currentUser?.id === a.id;
  const superCount = allAdmins.filter((x) => x.role === "super_admin").length;
  const isLastSuper = isSuper && superCount <= 1;

  const permsPill = (
    <div className="flex gap-1 flex-wrap max-w-full">
      {isSuper ? (
        <span className="text-[10px] px-2 py-1 rounded-full bg-[#c9a55c]/20 border border-[#c9a55c] text-[#5a4615]">
          {lang === "ar" ? "جميع الصلاحيات" : "All permissions"}
        </span>
      ) : (a.permissions || []).length === 0 ? (
        <span className="text-[10px] text-[#8a8a8a]">
          {lang === "ar" ? "لا صلاحيات" : "No permissions"}
        </span>
      ) : (
        // iter207 — show ALL permissions instead of slicing to 4.
        // The card grows to fit naturally; wrap keeps things tidy on
        // mobile RTL.
        (a.permissions || []).map((k) => {
          const meta = permMeta(k) || { key: k, ar: k, en: k };
          return (
            <span
              key={k}
              className="text-[10px] px-2 py-1 rounded-full bg-[#fbf7ec] border border-[#c9a55c]/40 text-[#5a4615]"
              title={meta.key}
            >
              {lang === "ar" ? meta.ar : meta.en}
            </span>
          );
        })
      )}
    </div>
  );

  const roleBadge = (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
        isSuper
          ? "bg-[#c9a55c]/20 text-[#5a4615] border border-[#c9a55c]/40"
          : "bg-[#0a0a0a]/[0.05] text-[#3a3a3a] border border-[#0a0a0a]/[0.10]"
      }`}
    >
      {isSuper ? "super_admin" : "admin"}
    </span>
  );

  const statusBadge = a.suspended ? (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
      <Ban className="w-3 h-3" /> {lang === "ar" ? "مُعلَّق" : "Suspended"}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <Play className="w-3 h-3" /> {lang === "ar" ? "نشط" : "Active"}
    </span>
  );

  const menu = (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setMenuOpen((v) => !v)}
        className="p-1.5 rounded-lg hover:bg-[#0a0a0a]/[0.05]"
        aria-label={lang === "ar" ? "خيارات" : "Actions"}
        data-testid={`admin-menu-${a.id}`}
      >
        <MoreVertical className="w-4 h-4 text-[#3a3a3a]" />
      </button>
      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute end-0 mt-1 w-52 bg-white rounded-xl border border-[#0a0a0a]/[0.10] shadow-lg z-30 overflow-hidden"
          data-testid={`admin-menu-panel-${a.id}`}
        >
          <MenuItem
            icon={Edit3}
            label={lang === "ar" ? "تعديل الحساب" : "Edit account"}
            onClick={() => { setDialog("edit"); setMenuOpen(false); }}
            testid={`menu-edit-${a.id}`}
          />
          {!isSuper && (
            <>
              <MenuItem
                icon={Plus}
                label={lang === "ar" ? "إضافة صلاحية" : "Add permission"}
                onClick={() => { setDialog("add"); setMenuOpen(false); }}
                testid={`menu-add-perm-${a.id}`}
              />
              <MenuItem
                icon={Minus}
                label={lang === "ar" ? "إزالة صلاحية" : "Remove permission"}
                onClick={() => { setDialog("remove"); setMenuOpen(false); }}
                disabled={(a.permissions || []).length === 0}
                testid={`menu-remove-perm-${a.id}`}
              />
            </>
          )}
          <MenuItem
            icon={Trash2}
            label={lang === "ar" ? "حذف الحساب" : "Delete account"}
            onClick={() => { setDialog("delete"); setMenuOpen(false); }}
            danger
            disabled={isSelf || isLastSuper}
            hint={
              isSelf
                ? (lang === "ar" ? "لا يمكنك حذف نفسك" : "Can't delete yourself")
                : isLastSuper
                  ? (lang === "ar" ? "آخر Super Admin" : "Last super admin")
                  : null
            }
            testid={`menu-delete-${a.id}`}
          />
        </div>
      )}
    </div>
  );

  const dialogs = (
    <>
      {dialog === "edit" && (
        <EditAdminDialog
          a={a} lang={lang} onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); onReload(); }}
          isSelf={isSelf} isLastSuper={isLastSuper}
        />
      )}
      {dialog === "add" && (
        <AddPermsDialog a={a} lang={lang} onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); onReload(); }} />
      )}
      {dialog === "remove" && (
        <RemovePermsDialog a={a} lang={lang} onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); onReload(); }} />
      )}
      {dialog === "delete" && (
        <DeleteAdminDialog a={a} lang={lang} onClose={() => setDialog(null)}
          onDeleted={() => { setDialog(null); onReload(); }} />
      )}
    </>
  );

  if (mode === "card") {
    return (
      <div className="rounded-xl bg-white border border-[#0a0a0a]/[0.08] p-4" data-testid={`admin-card-${a.id}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="font-semibold text-[14px] text-[#0a0a0a] truncate">{a.name}</div>
            <div className="text-[12px] text-[#525252] truncate">{a.email}</div>
          </div>
          {menu}
        </div>
        <div className="flex items-center gap-2 mb-3">
          {roleBadge}
          {statusBadge}
        </div>
        <div>{permsPill}</div>
        {dialogs}
      </div>
    );
  }

  return (
    <tr className="border-t border-[#0a0a0a]/[0.06]" data-testid={`admin-row-${a.id}`}>
      <td className="p-3 whitespace-nowrap">{a.name}</td>
      <td className="p-3 text-[#3a3a3a]">{a.email}</td>
      <td className="p-3">{roleBadge}</td>
      <td className="p-3">{permsPill}</td>
      <td className="p-3">{statusBadge}</td>
      <td className="p-3">
        <div className="flex justify-end">{menu}</div>
        {dialogs}
      </td>
    </tr>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger, disabled, hint, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-start px-3 py-2 flex items-center gap-2 text-[13px] transition-colors ${
        danger ? "text-red-700 hover:bg-red-50" : "text-[#3a3a3a] hover:bg-[#fafaf7]"
      } ${disabled ? "opacity-50 cursor-not-allowed hover:bg-transparent" : ""}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {hint && <span className="text-[10px] text-[#8a8a8a]">({hint})</span>}
    </button>
  );
}

// ── Modal shell ─────────────────────────────────────────────────────
function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div
        className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#0a0a0a]/[0.08] sticky top-0 bg-white z-10">
          <h3 className="font-display text-lg">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#0a0a0a]/[0.05]" data-testid="modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Edit admin dialog ──────────────────────────────────────────────
function EditAdminDialog({ a, lang, onClose, onSaved, isSelf, isLastSuper }) {
  const [name, setName] = useState(a.name || "");
  const [email, setEmail] = useState(a.email || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [role, setRole] = useState(a.role || "admin");
  const [suspended, setSuspended] = useState(!!a.suspended);
  const [busy, setBusy] = useState(false);

  const roleLocked = isSelf || (a.role === "super_admin" && isLastSuper);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { name, email };
      if (password) body.password = password;
      if (role !== a.role) body.role = role;
      if (suspended !== !!a.suspended) body.suspended = suspended;
      await apiClient.patch(`/admin/admins/${a.id}`, body);
      toast.success(lang === "ar" ? "تم حفظ التغييرات" : "Changes saved");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (typeof d === "string" ? d : d.ar || d.en)) || (lang === "ar" ? "تعذّر الحفظ" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={lang === "ar" ? "تعديل الحساب" : "Edit account"} onClose={onClose} testid={`edit-modal-${a.id}`}>
      <div className="p-5 space-y-3">
        <Field label={lang === "ar" ? "الاسم" : "Name"}>
          <input className="input-dark w-full" value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name" />
        </Field>
        <Field label={lang === "ar" ? "البريد الإلكتروني" : "Email"}>
          <input className="input-dark w-full" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-email" />
        </Field>
        <Field label={lang === "ar" ? "كلمة مرور جديدة (اختياري)" : "New password (optional)"}>
          <div className="relative">
            <input
              className="input-dark w-full pe-10"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={lang === "ar" ? "8 أحرف على الأقل" : "At least 8 characters"}
              data-testid="edit-password"
            />
            <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute end-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#0a0a0a]/[0.05]">
              {showPw ? <EyeOff className="w-4 h-4 text-[#6b6b6b]" /> : <Eye className="w-4 h-4 text-[#6b6b6b]" />}
            </button>
          </div>
        </Field>
        <Field label={lang === "ar" ? "الدور" : "Role"} hint={roleLocked ? (lang === "ar" ? "لا يمكن تغيير هذا الدور" : "Locked") : null}>
          <select className="input-dark w-full" value={role} disabled={roleLocked} onChange={(e) => setRole(e.target.value)} data-testid="edit-role">
            <option value="admin">admin</option>
            <option value="super_admin">super_admin</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={suspended} disabled={isSelf} onChange={(e) => setSuspended(e.target.checked)} className="accent-[#c9a55c]" data-testid="edit-suspended" />
          <span>{lang === "ar" ? "تعليق الحساب" : "Suspend account"}</span>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2 p-4 border-t border-[#0a0a0a]/[0.08]">
        <button onClick={onClose} className="btn-ghost text-sm" data-testid="edit-cancel">
          {lang === "ar" ? "إلغاء" : "Cancel"}
        </button>
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="edit-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {lang === "ar" ? "حفظ" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="text-[12px] font-semibold text-[#404040] mb-1 flex items-center gap-1">
        {label}
        {hint && <span className="text-[11px] text-[#8a8a8a]">— {hint}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Add permissions dialog ────────────────────────────────────────
function AddPermsDialog({ a, lang, onClose, onSaved }) {
  const current = new Set(a.permissions || []);
  const available = PERM_GROUPS
    .map((g) => ({ ...g, perms: g.perms.filter((p) => !current.has(p.key)) }))
    .filter((g) => g.perms.length > 0);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (k) => {
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };
  const save = async () => {
    if (busy || picked.size === 0) return;
    setBusy(true);
    try {
      await apiClient.post(`/admin/admins/${a.id}/permissions/add`, { permissions: [...picked] });
      toast.success(lang === "ar" ? `تمت إضافة ${picked.size} صلاحية` : `${picked.size} permission(s) added`);
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (typeof d === "string" ? d : d.ar || d.en)) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={lang === "ar" ? `إضافة صلاحية — ${a.name}` : `Add permission — ${a.name}`} onClose={onClose} testid={`add-perms-modal-${a.id}`} wide>
      <div className="p-5">
        {available.length === 0 ? (
          <div className="text-center py-8 text-[13px] text-[#8a8a8a]" data-testid="no-ungranted-perms">
            {lang === "ar" ? "هذا الحساب يمتلك جميع الصلاحيات بالفعل." : "This account already has every permission."}
          </div>
        ) : (
          <div className="space-y-3">
            {available.map((g) => (
              <div key={g.id} className="rounded-xl border border-[#0a0a0a]/[0.08] p-3">
                <div className="text-[12px] font-semibold text-[#8a6f2e] mb-2">{lang === "ar" ? g.ar : g.en}</div>
                <div className="grid gap-1.5">
                  {g.perms.map((p) => {
                    const on = picked.has(p.key);
                    return (
                      <label
                        key={p.key}
                        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-[13px] cursor-pointer ${
                          on ? "bg-[#fbf7ec] border-[#c9a55c]/50" : "border-[#0a0a0a]/[0.06] hover:bg-[#fafaf7]"
                        }`}
                        data-testid={`add-perm-${p.key}`}
                      >
                        <input type="checkbox" checked={on} onChange={() => toggle(p.key)} className="accent-[#c9a55c]" />
                        <span>{lang === "ar" ? p.ar : p.en}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 p-4 border-t border-[#0a0a0a]/[0.08]">
        <span className="text-[11px] text-[#8a8a8a]">{lang === "ar" ? `المحدد: ${picked.size}` : `Selected: ${picked.size}`}</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="btn-ghost text-sm">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
          <button onClick={save} disabled={busy || picked.size === 0} className="btn-primary text-sm" data-testid="add-perms-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {lang === "ar" ? "إضافة" : "Add"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Remove permissions dialog ─────────────────────────────────────
function RemovePermsDialog({ a, lang, onClose, onSaved }) {
  const currentKeys = a.permissions || [];
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (k) => {
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };
  const wouldLeaveEmpty = currentKeys.length > 0 && picked.size >= currentKeys.length;
  const save = async () => {
    if (busy || picked.size === 0 || wouldLeaveEmpty) return;
    setBusy(true);
    try {
      await apiClient.post(`/admin/admins/${a.id}/permissions/remove`, { permissions: [...picked] });
      toast.success(lang === "ar" ? `تمت إزالة ${picked.size} صلاحية` : `${picked.size} permission(s) removed`);
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (typeof d === "string" ? d : d.ar || d.en)) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={lang === "ar" ? `إزالة صلاحية — ${a.name}` : `Remove permission — ${a.name}`} onClose={onClose} testid={`remove-perms-modal-${a.id}`}>
      <div className="p-5">
        {currentKeys.length === 0 ? (
          <div className="text-center py-8 text-[13px] text-[#8a8a8a]">
            {lang === "ar" ? "لا توجد صلاحيات لإزالتها." : "No permissions to remove."}
          </div>
        ) : (
          <div className="space-y-1.5">
            {currentKeys.map((k) => {
              const meta = permMeta(k) || { key: k, ar: k, en: k };
              const on = picked.has(k);
              return (
                <label
                  key={k}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-[13px] cursor-pointer ${
                    on ? "bg-red-50 border-red-200" : "border-[#0a0a0a]/[0.06] hover:bg-[#fafaf7]"
                  }`}
                  data-testid={`remove-perm-${k}`}
                >
                  <input type="checkbox" checked={on} onChange={() => toggle(k)} />
                  <span className="flex-1">{lang === "ar" ? meta.ar : meta.en}</span>
                  <span className="text-[10px] text-[#8a8a8a]">{meta.key}</span>
                </label>
              );
            })}
          </div>
        )}
        {wouldLeaveEmpty && (
          <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="remove-perms-warning">
            {lang === "ar"
              ? "لا يمكن إزالة جميع الصلاحيات — يجب أن يحتفظ Admin بصلاحية واحدة على الأقل."
              : "Cannot remove every permission — an admin must retain at least one."}
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 p-4 border-t border-[#0a0a0a]/[0.08]">
        <button onClick={onClose} className="btn-ghost text-sm">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
        <button
          onClick={save}
          disabled={busy || picked.size === 0 || wouldLeaveEmpty}
          className="btn-primary text-sm bg-red-700 hover:bg-red-800"
          data-testid="remove-perms-save"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Minus className="w-4 h-4" />}
          {lang === "ar" ? "إزالة" : "Remove"}
        </button>
      </div>
    </Modal>
  );
}

// ── Delete confirmation dialog ────────────────────────────────────
function DeleteAdminDialog({ a, lang, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await apiClient.delete(`/admin/admins/${a.id}`);
      toast.success(lang === "ar" ? "تم حذف الحساب" : "Account deleted");
      onDeleted();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (typeof d === "string" ? d : d.ar || d.en)) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={lang === "ar" ? "تأكيد الحذف" : "Confirm deletion"} onClose={onClose} testid={`delete-modal-${a.id}`}>
      <div className="p-5 text-[13px] text-[#3a3a3a] leading-relaxed">
        {lang === "ar"
          ? <>هل تريد بالتأكيد حذف الحساب الإداري <b>{a.name}</b> ({a.email})؟ هذا الإجراء لا يمكن التراجع عنه.</>
          : <>Are you sure you want to delete the admin <b>{a.name}</b> ({a.email})? This cannot be undone.</>}
      </div>
      <div className="flex items-center justify-end gap-2 p-4 border-t border-[#0a0a0a]/[0.08]">
        <button onClick={onClose} className="btn-ghost text-sm">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
        <button onClick={del} disabled={busy} className="btn-primary text-sm bg-red-700 hover:bg-red-800" data-testid="delete-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {lang === "ar" ? "حذف" : "Delete"}
        </button>
      </div>
    </Modal>
  );
}
